import React, { useState, useEffect, useCallback, useRef } from "react";
import { Download, FileSpreadsheet, Mail, Printer } from "lucide-react";
import DetachedModal from "../../../../Shared/Modals/DetachedModal/DetachedModal";
import { EDSButton } from "../../../../eds/EDSButton";
import Loading from "../../../../../utils/Loading";
import { InvoiceViewOptions } from "../../../../../types/invoices";
import { getInvoicePrintHtml } from "../../../../../APIs/Booking/InvoiceOperations";

const DISPLAY_OPTIONS: Array<{
  key: keyof InvoiceViewOptions;
  label: string;
}> = [
  { key: "showAmountDue", label: "Show Amount Due" },
  { key: "invoiceIsCopy", label: "Copy" },
  { key: "invoiceIsProForma", label: "Pro Forma" }
];

interface InvoiceViewModalProps {
  bookingId: string;
  invoiceId: string;
  onClose: () => void;
  onPrint: (options: InvoiceViewOptions) => void;
  onDownload: (options: InvoiceViewOptions) => void;
  onDownloadCsv: (options: InvoiceViewOptions) => void;
  onEmail: () => void;
  isPrinting: boolean;
  isDownloading: boolean;
  isDownloadingCsv: boolean;
  isEmailing: boolean;
}

const InvoiceViewModal = ({
  bookingId,
  invoiceId,
  onClose,
  onPrint,
  onDownload,
  onDownloadCsv,
  onEmail,
  isPrinting,
  isDownloading,
  isDownloadingCsv,
  isEmailing
}: InvoiceViewModalProps): React.JSX.Element => {
  const [options, setOptions] = useState<InvoiceViewOptions>({
    showAmountDue: true,
    invoiceIsCopy: false,
    invoiceIsProForma: false
  });
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Track the latest request so stale responses from earlier calls
  // (e.g. due to React.StrictMode double-invocation) are ignored.
  const requestIdRef = useRef(0);

  const handleIframeLoad = useCallback((): void => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const iframeDoc =
        iframe.contentDocument ?? iframe.contentWindow?.document;
      if (iframeDoc?.body) {
        // Auto-resize iframe to fit content
        const contentHeight = iframeDoc.documentElement.scrollHeight;
        if (contentHeight > 0) {
          iframe.style.height = `${contentHeight + 32}px`;
        }
      }
    } catch {
      // Cross-origin restrictions may prevent access; keep default height
    }
  }, []);

  const fetchPreviewHtml = useCallback(
    async (viewOptions: InvoiceViewOptions): Promise<void> => {
      if (!bookingId || !invoiceId) return;

      // Increment request ID so any in-flight request becomes stale
      const currentRequestId = ++requestIdRef.current;

      setIsLoadingPreview(true);
      setPreviewError(null);

      try {
        const html = await getInvoicePrintHtml(
          bookingId,
          invoiceId,
          viewOptions
        );

        // Ignore the result if a newer request has been issued
        if (currentRequestId !== requestIdRef.current) return;

        if (html) {
          setPreviewHtml(html);
          setPreviewError(null);
        } else {
          setPreviewError("Failed to load invoice preview.");
        }
      } catch {
        if (currentRequestId !== requestIdRef.current) return;
        setPreviewError("An error occurred while loading the invoice preview.");
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoadingPreview(false);
        }
      }
    },
    [bookingId, invoiceId]
  );

  // Fetch preview HTML on mount
  useEffect(() => {
    fetchPreviewHtml(options);
    return () => {
      // Invalidate any in-flight request when the effect cleans up
      // (handles React.StrictMode double-invocation and unmount)
      requestIdRef.current++;
    };
    // Only fetch on mount — option changes are handled by handleOptionChange
  }, []);

  const handleOptionChange = (
    key: keyof InvoiceViewOptions,
    checked: boolean
  ): void => {
    const updated = { ...options, [key]: checked };

    // Copy and Pro Forma are mutually exclusive (matches collins-ui)
    if (key === "invoiceIsCopy" && checked) {
      updated.invoiceIsProForma = false;
    } else if (key === "invoiceIsProForma" && checked) {
      updated.invoiceIsCopy = false;
    }

    setOptions(updated);
    // Re-fetch preview when display options change (matches collins-ui refreshApiInvoiceHtml)
    fetchPreviewHtml(updated);
  };

  const isAnyActionInProgress =
    isPrinting || isDownloading || isDownloadingCsv || isEmailing;

  return (
    <DetachedModal
      heading="Invoice Options"
      width="min(90vw, 960px)"
      handleClose={onClose}
    >
      <div className="flex flex-col gap-6 p-1">
        <div className="flex flex-col gap-3">
          <h4 className="text-sm font-semibold text-muted-foreground">
            Display Options
          </h4>

          <div className="grid grid-cols-3 gap-2">
            {DISPLAY_OPTIONS.map((option) => (
              <EDSButton
                key={option.key}
                variant={options[option.key] ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  handleOptionChange(option.key, !options[option.key])
                }
                disabled={isAnyActionInProgress}
                aria-pressed={options[option.key]}
                aria-label={option.label}
              >
                {option.label}
              </EDSButton>
            ))}
          </div>
        </div>

        <section
          className="rounded border border-[var(--borderColor)] bg-white min-h-[400px] relative"
          aria-label="Invoice preview"
        >
          {isLoadingPreview && (
            <div className="flex items-center justify-center p-8">
              <Loading />
            </div>
          )}

          {previewError && !isLoadingPreview && (
            <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
              {previewError}
            </div>
          )}

          {previewHtml && !isLoadingPreview && (
            <iframe
              ref={iframeRef}
              title="Invoice preview"
              srcDoc={previewHtml}
              sandbox=""
              className="w-full border-0"
              style={{ minHeight: "500px", height: "70vh" }}
              onLoad={handleIframeLoad}
            />
          )}
        </section>

        <div className="flex flex-col gap-3">
          <h4 className="text-sm font-semibold text-muted-foreground">
            Actions
          </h4>

          <div className="flex flex-wrap gap-2">
            <EDSButton
              variant="outline"
              size="sm"
              onClick={() => onPrint(options)}
              disabled={isAnyActionInProgress}
              aria-label="Print invoice"
            >
              <Printer className="w-4 h-4" />
              {isPrinting ? "Printing..." : "Print"}
            </EDSButton>

            <EDSButton
              variant="outline"
              size="sm"
              onClick={() => onDownload(options)}
              disabled={isAnyActionInProgress}
              aria-label="Download invoice as PDF"
            >
              <Download className="w-4 h-4" />
              {isDownloading ? "Downloading..." : "Download PDF"}
            </EDSButton>

            <EDSButton
              variant="outline"
              size="sm"
              onClick={() => onDownloadCsv(options)}
              disabled={isAnyActionInProgress}
              aria-label="Download invoice as CSV"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {isDownloadingCsv ? "Downloading..." : "Download CSV"}
            </EDSButton>

            <EDSButton
              variant="outline"
              size="sm"
              onClick={onEmail}
              disabled={isAnyActionInProgress}
              aria-label="Email invoice to customer"
            >
              <Mail className="w-4 h-4" />
              {isEmailing ? "Sending..." : "Email"}
            </EDSButton>
          </div>
        </div>
      </div>
    </DetachedModal>
  );
};

export default InvoiceViewModal;
